/**
 * Copy deck for the evidence progress card family (evidence-progress-spec.md §Copy L78-96).
 * Pure module — no Angular dependencies, no side effects, templates never hardcode any of
 * this text (feature acceptance criterion 6). Every visible string of the card resolves
 * here; raw ids (`tool_id`, task ids, unknown stage/status vocabulary) enter ONLY as
 * interpolation parameters, never as copy of their own.
 *
 * The spec's per-tool keys (`evidence.tool.<tool_id>.running` / `.done`) are lookup-based:
 * `EVIDENCE_TOOL_COPY` holds the cataloged overrides and every missing key falls back to the
 * generic deck keys with the raw tool id as parameter (`Running {tool}…` / `Done`).
 */

/** English default copy, keyed by the spec deck (L78-96) plus the anatomy-only parameters
 *  the card needs (header subtitle, error-title stage parameters, icon state words). */
export const EVIDENCE_COPY = {
  /** `evidence.title.running` — "Running {tool}…" with the raw tool id as parameter. */
  titleRunning: "Running {tool}…",
  /** `evidence.title.error` — "Couldn’t finish {stage}" with the human stage phrase as parameter. */
  titleError: "Couldn’t finish {stage}",
  /** `evidence.stage.api_call.running`. */
  stageApiCallRunning: "Fetching data…",
  /** `evidence.stage.api_call.completed`. */
  stageApiCallCompleted: "Data fetched",
  /** `evidence.stage.staging.running`. */
  stageStagingRunning: "Saving result…",
  /** `evidence.stage.staging.completed`. */
  stageStagingCompleted: "Result saved",
  /** `evidence.stage.error.hint` — the calm error-state subtitle. */
  stageErrorHint: "The plan will report the outcome below.",
  /** `evidence.stage.long.hint` — the >20 s running-stage note. */
  stageLongHint: "Large downloads can take a minute or more.",
  /** `evidence.summary` — "{what} · {size} · {duration}". */
  summary: "{what} · {size} · {duration}",
  /** `evidence.summary.batch` — "{what} · {rows} rows · {size} · {duration}" (aggregate form). */
  summaryBatch: "{what} · {rows} rows · {size} · {duration}",
  /** `evidence.row` — "Row {n}" (0-based row_index rendered 1-based). */
  row: "Row {n}",
  /** `evidence.row.overflow` — "+{n} more" chip copy. */
  rowOverflow: "+{n} more",
  /** `evidence.generic` — the no-tool-id title fallback. */
  generic: "Working on it…",
  /** Header subtitle segment for the plan task id (anatomy L31: "task id"). */
  subtitleTask: "task {task}",
  /** `evidence.combined.subtitle` — subtitle of a deliberate multi-task (null task_id) episode. */
  combinedSubtitle: "Combined execution",
  /** `evidence.repeat.tooltip` — tooltip of the repeat marker on stacked (occurrence > 0) episodes. */
  repeatTooltip: "Repeated execution",
  /** `evidence.title.error` parameter phrase for an errored `api_call` stage. */
  errorParamApiCall: "fetching the data",
  /** `evidence.title.error` parameter phrase for an errored `staging` stage. */
  errorParamStaging: "saving the result",
  /** State word for the pending stage-row icon tooltip. */
  wordPending: "Pending",
  /** State word for the running icon tooltips (stage row + header). */
  wordRunning: "Running",
  /** State word for the completed icon tooltips (stage row + header). */
  wordCompleted: "Completed",
  /** State word for the error icon tooltips. */
  wordError: "Error",
} as const;

/**
 * Per-tool title overrides (`evidence.tool.<tool_id>.running` / `.done`). Empty for now —
 * every uncataloged tool falls back to the generic deck copy with the raw tool id as
 * parameter (spec L93). Add entries here as tools are cataloged; never in templates.
 */
const EVIDENCE_TOOL_COPY: Readonly<Record<string, { running: string; done: string }>> = {};

/** Interpolates the first `{token}`-style placeholder of a deck template. */
const withParam = (template: string, token: string, value: string): string => template.replace(`{${token}}`, value);

/**
 * Running-state header title for one tool call (spec L81/L93): the per-tool `running` copy
 * when cataloged, else `Running {tool}…` with the raw tool id as parameter; a call without a
 * tool id falls back to `evidence.generic` (`Working on it…`).
 */
export function evidenceRunningTitle(toolId: string | null): string {
  if (toolId == null) return EVIDENCE_COPY.generic;
  return EVIDENCE_TOOL_COPY[toolId]?.running ?? withParam(EVIDENCE_COPY.titleRunning, "tool", toolId);
}

/**
 * Completed-state `what` segment of the summary line (spec L58/L93): the per-tool `done`
 * copy when cataloged, else the generic `Done` fallback.
 */
export function evidenceDoneTitle(toolId: string | null): string {
  if (toolId == null) return "Done";
  return EVIDENCE_TOOL_COPY[toolId]?.done ?? "Done";
}

/**
 * Calm error-state header title (spec L49/L82): `Couldn’t finish {stage}` where {stage} is a
 * human phrase — "fetching the data" / "saving the result" for the known stages, the raw tool
 * id for a failed container call, and the raw stage id for unknown future stages.
 */
export function evidenceErrorTitle(stage: string, toolId: string | null): string {
  let param: string;
  switch (stage) {
    case "api_call":
      param = EVIDENCE_COPY.errorParamApiCall;
      break;
    case "staging":
      param = EVIDENCE_COPY.errorParamStaging;
      break;
    case "execute":
      param = toolId ?? stage;
      break;
    default:
      param = stage;
  }
  return withParam(EVIDENCE_COPY.titleError, "stage", param);
}

/**
 * Stage-row label for one stage/status pair (spec L83-86): the deck's present/past copy for
 * the known stages (`api_call`, `staging`); unknown future stages tolerate their raw stage id
 * verbatim as the label. Anything not `completed` renders the present-tense label.
 */
export function evidenceStageLabel(stage: string, status: string): string {
  if (stage === "api_call")
    return status === "completed" ? EVIDENCE_COPY.stageApiCallCompleted : EVIDENCE_COPY.stageApiCallRunning;
  if (stage === "staging")
    return status === "completed" ? EVIDENCE_COPY.stageStagingCompleted : EVIDENCE_COPY.stageStagingRunning;
  return stage;
}

/** `evidence.row` — the 1-based display label of one batch row (`Row {n}` from a 0-based index). */
export function evidenceRowLabel(rowIndex: number): string {
  return withParam(EVIDENCE_COPY.row, "n", String(rowIndex + 1));
}

/**
 * Header subtitle (anatomy L31): `task <task_id>` and, for batch calls, the `evidence.row`
 * label — joined with " · ". A null task_id is a deliberate multi-task (combined) execution
 * (v1.1 S7 — semantic, never missing data), so it renders `evidence.combined.subtitle` in the
 * task-id slot instead of an empty subtitle line.
 */
export function evidenceSubtitle(taskId: string | null, rowIndex: number | null): string | null {
  const parts: string[] = [];
  parts.push(taskId != null ? withParam(EVIDENCE_COPY.subtitleTask, "task", taskId) : EVIDENCE_COPY.combinedSubtitle);
  if (rowIndex != null) parts.push(evidenceRowLabel(rowIndex));
  return parts.join(" · ");
}

/** `evidence.row.overflow` — the "+{n} more" chip copy for rows beyond the shown window. */
export function evidenceRowOverflowLabel(count: number): string {
  return withParam(EVIDENCE_COPY.rowOverflow, "n", String(count));
}

/**
 * `evidence.summary` (L89) generalized to the card's render model: `what` first, then every
 * non-null part joined with " · ". Null parts drop out entirely (spec: size omitted when
 * null); the batch row label rides as an extra part under the store's separable-row contract.
 */
export function evidenceSummaryLine(what: string, parts: ReadonlyArray<string | null>): string {
  return [what, ...parts.filter((part): part is string => part != null)].join(" · ");
}

/** `evidence.summary.batch` (L90) — the aggregate form, kept for deck completeness. */
export function evidenceBatchSummaryLine(
  what: string,
  rows: number,
  size: string | null,
  duration: string | null,
): string {
  const line = withParam(EVIDENCE_COPY.summaryBatch, "rows", String(rows));
  return evidenceSummaryLine(withParam(line, "what", what), [size, duration]);
}

/**
 * State word for an icon tooltip (spec L107: `matTooltip` with the state name on loose
 * icons). Known statuses map to the deck's state words; unknown future statuses tolerate
 * their raw string verbatim as the tooltip word.
 */
export function evidenceStateWord(status: string): string {
  switch (status) {
    case "running":
      return EVIDENCE_COPY.wordRunning;
    case "completed":
      return EVIDENCE_COPY.wordCompleted;
    case "error":
      return EVIDENCE_COPY.wordError;
    case "pending":
      return EVIDENCE_COPY.wordPending;
    default:
      return status;
  }
}
